// MACHINERY V2 (Stroje) — the type-gated field sets (M2.7).
//
// Spec: data package §10 FIELD TABLE, row for row. Reference impl:
// components/machineTypeForms/CncForm.tsx + ProductionLineForm.tsx (validation
// rules copied from them verbatim). Artboards 0803 (CNC) / 0804 (Linka) fix the
// ORDER, the two-column grid and the labels; 0802 fixes the dimmed state.
//
// >>> THREE ARMS, NOT TWO: NO TYPE -> NO FIELD SET AT ALL. <<<
// `isLine` is false both for "CNC" and for "nothing chosen yet", so a bare
// `isLine ? … : …` would render the whole CNC set (dimmed) before the user has
// picked anything. Artboard 0802 draws no CNC row, and the designer's own
// guards are `{isCnc && …}` / `{isLine && …}` with the shared serial / year /
// control-system block INSIDE each one (machinery-catalog.jsx:242, :277) —
// neither renders until `stage` is "cnc" or "line" (:209). So `typeChosen`
// gates the type-specific sets AND the shared tail, and the gated region holds
// exactly one field — Lokácia — until a type is chosen.
//
// >>> THE FIELD SETS DIVERGE EXACTLY AS §10 SAYS. <<<
//   CNC   adds axisConfigurationId, and customBrandName when the chosen
//         manufacturer is "Other" (CNC ONLY — ProductionLineForm has no such
//         branch).
//   LINKA adds numberOfStations and automationLevelId. §10: "ProductionLineForm
//         ONLY renders [automationLevelId] (CNC leaves it undefined though the
//         options load)" — so CNC does not render it and the CNC payload
//         carries undefined, which is what the resolver in
//         useCustomMachineForm.ts produces.
//   BOTH  carry serialNumber, productionYear and the two control-system fields.
//
// The four fields BOTH sets carry live in CustomMachineTail.tsx (300-line cap).
//
// ARTBOARD-OVER-v1 COPY CALLS, all placeholders, none touching a binding:
// 0803 writes "napr. DMU 75" for the model (v1: "DMU75") and 0804 "napr. 6" for
// the station count — where v1 reuses the SERIAL placeholder "123456"
// (ProductionLineForm.tsx:125). The artboard governs look and copy, so v2
// follows it; every validation rule is unchanged. (The serial and year
// placeholders make the same call in CustomMachineTail.tsx.)
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { UseFormReturn } from "react-hook-form";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { upToTenThousandRegex } from "../../../../shared/validators";
import { ChangeMachine } from "../../models/models";
import { PinGlyph } from "../fleet/fleetCardAtoms";
import FormField from "../forms/FormField";
import FormSelect, { FormSelectOption } from "../forms/FormSelect";
import { AxisGlyph, BoxGlyph, GearGlyph, HashGlyph, TagGlyph } from "../forms/FormGlyphs";
import { SelectRow, TextRow, TwoCol } from "./customMachineFieldAtoms";
import CustomMachineTail from "./CustomMachineTail";

export interface CustomMachineFieldsProps {
  form: UseFormReturn<ChangeMachine>;
  /** False until `typeId` is picked — see the three-arm note in the header. */
  typeChosen: boolean;
  isLine: boolean;
  /** Manufacturer whose name is exactly "Other" — unlocks customBrandName. */
  showCustomBrand: boolean;
  locationId: string | null;
  onLocationChange: (id?: string) => void;
  locationOpts: FormSelectOption[];
  manufacturerOpts: FormSelectOption[];
  operationTypeOpts: FormSelectOption[];
  axisOpts: FormSelectOption[];
  controlBrandOpts: FormSelectOption[];
  automationOpts: FormSelectOption[];
  controlModelNames: string[];
  /** §10: the control-system MODEL is disabled while the brand is exactly "". */
  systemDisabled: boolean;
}

const CustomMachineFields: React.FC<CustomMachineFieldsProps> = ({
  form,
  typeChosen,
  isLine,
  showCustomBrand,
  locationId,
  onLocationChange,
  locationOpts,
  manufacturerOpts,
  operationTypeOpts,
  axisOpts,
  controlBrandOpts,
  automationOpts,
  controlModelNames,
  systemDisabled,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const required = translate("common:forms.required");
  const chooseFromList = translate("myMachinery:chooseFromList");

  return (
    <React.Fragment>
      <FormField glyph={<PinGlyph />} label={translate("myMachinery:location")} htmlFor="locationId">
        <FormSelect
          id="locationId"
          ariaLabel={translate("myMachinery:location")}
          placeholder={translate("myMachinery:v2.add.locationPlaceholder")}
          value={locationId || undefined}
          options={locationOpts}
          clearLabel={translate("myMachinery:v2.add.locationClear")}
          onChange={onLocationChange}
        />
      </FormField>

      {!typeChosen ? null : isLine ? (
        <React.Fragment>
          <TwoCol>
            <SelectRow
              form={form}
              name="manufacturerId"
              label={translate("myMachinery:manufacturer")}
              glyph={<TagGlyph />}
              required
              placeholder={chooseFromList}
              options={manufacturerOpts}
            />
            <TextRow
              form={form}
              name="model"
              label={translate("myMachinery:lineNameModel")}
              glyph={<BoxGlyph />}
              required
              maxLength={50}
              placeholder={translate("myMachinery:v2.add.linePlaceholder")}
              rules={{ required }}
            />
          </TwoCol>

          <SelectRow
            form={form}
            name="operationTypeId"
            label={translate("myMachinery:operationType")}
            glyph={<GearGlyph />}
            required
            placeholder={chooseFromList}
            options={operationTypeOpts}
          />

          <TwoCol>
            <TextRow
              form={form}
              name="numberOfStations"
              label={translate("myMachinery:noOfStations")}
              glyph={<HashGlyph />}
              required
              mono
              maxLength={10}
              placeholder={translate("myMachinery:v2.add.stationsPlaceholder")}
              rules={{
                required,
                pattern: {
                  value: upToTenThousandRegex,
                  message: translate("myMachinery:stationsRange"),
                },
              }}
            />
            <SelectRow
              form={form}
              name="automationLevelId"
              label={translate("myMachinery:automationLevel")}
              glyph={<GearGlyph />}
              required
              placeholder={chooseFromList}
              options={automationOpts}
            />
          </TwoCol>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <TwoCol>
            <SelectRow
              form={form}
              name="manufacturerId"
              label={translate("common:brand")}
              glyph={<TagGlyph />}
              required
              placeholder={chooseFromList}
              options={manufacturerOpts}
            />
            <TextRow
              form={form}
              name="model"
              label={translate("myMachinery:model")}
              glyph={<BoxGlyph />}
              required
              maxLength={50}
              placeholder={translate("myMachinery:v2.add.modelPlaceholder")}
              rules={{ required }}
            />
          </TwoCol>

          {showCustomBrand && (
            <TextRow
              form={form}
              name="customBrandName"
              label={translate("common:brand")}
              glyph={<TagGlyph />}
              required
              maxLength={50}
              rules={{ required }}
            />
          )}

          <TwoCol>
            <SelectRow
              form={form}
              name="operationTypeId"
              label={translate("myMachinery:operationType")}
              glyph={<GearGlyph />}
              required
              placeholder={chooseFromList}
              options={operationTypeOpts}
            />
            <SelectRow
              form={form}
              name="axisConfigurationId"
              label={translate("myMachinery:axisConfiguration")}
              glyph={<AxisGlyph />}
              required
              placeholder={chooseFromList}
              options={axisOpts}
              // §10 QUIRK, PRESERVED: v1's axis Autocomplete reacts to a pick
              // with a bare `clearErrors()` (CncForm.tsx:137) — no argument, so
              // it clears EVERY field's error, not just this one. Kept as-is;
              // §10 lists it as a quirk to preserve, not a bug to fix here.
              onPicked={() => form.clearErrors()}
            />
          </TwoCol>
        </React.Fragment>
      )}

      {typeChosen && (
        <CustomMachineTail
          form={form}
          controlBrandOpts={controlBrandOpts}
          controlModelNames={controlModelNames}
          systemDisabled={systemDisabled}
        />
      )}
    </React.Fragment>
  );
};

export default CustomMachineFields;
